/**
 * Feedback Animations System
 * 
 * Provides smooth, accessible animations for validation state transitions.
 * Includes error shake effects, success animations, loading states, and
 * respects user preferences for reduced motion.
 */

/**
 * Animation types supported by the system
 */
export type AnimationType = 'shake' | 'pulse' | 'fade' | 'slide' | 'bounce' | 'glow' | 'loading';

/**
 * Animation configuration
 */
export interface AnimationConfig {
  /** Animation type */
  type: AnimationType;
  /** Duration in milliseconds */
  duration?: number;
  /** Animation easing function */
  easing?: string;
  /** Delay before starting animation */
  delay?: number;
  /** Number of iterations (0 = infinite) */
  iterations?: number;
  /** Animation direction */
  direction?: 'normal' | 'reverse' | 'alternate' | 'alternate-reverse';
}

/**
 * Animation state tracking
 */
interface AnimationState {
  /** Currently running animations */
  activeAnimations: Animation[];
  /** Element being animated */
  element: HTMLElement;
  /** Animation configuration */
  config: AnimationConfig;
  /** Start time */
  startTime: number;
  /** Whether animation respects reduced motion */
  respectsReducedMotion: boolean;
}

/**
 * Pre-defined animation configurations
 */
const ANIMATION_PRESETS: Record<string, AnimationConfig> = {
  // Error animations
  'error-shake': {
    type: 'shake',
    duration: 500,
    easing: 'ease-in-out',
    iterations: 1
  },
  'error-pulse': {
    type: 'pulse',
    duration: 600,
    easing: 'ease-in-out',
    iterations: 2
  },

  // Success animations
  'success-glow': {
    type: 'glow',
    duration: 800,
    easing: 'ease-out',
    iterations: 1
  },
  'success-bounce': {
    type: 'bounce',
    duration: 600,
    easing: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    iterations: 1
  },

  // Loading animations
  'loading-pulse': {
    type: 'pulse',
    duration: 1000,
    easing: 'ease-in-out',
    iterations: 0, // Infinite
    direction: 'alternate'
  },

  // Focus animations
  'focus-glow': {
    type: 'glow',
    duration: 300,
    easing: 'ease-out',
    iterations: 1
  },

  // Warning animations
  'warning-fade': {
    type: 'fade',
    duration: 400,
    easing: 'ease-in-out',
    iterations: 2,
    direction: 'alternate'
  }
};

/**
 * CSS keyframe definitions for animations
 */
const KEYFRAMES: Record<AnimationType, Keyframe[]> = {
  shake: [
    { transform: 'translateX(0)' },
    { transform: 'translateX(-4px)' },
    { transform: 'translateX(4px)' },
    { transform: 'translateX(-4px)' },
    { transform: 'translateX(4px)' },
    { transform: 'translateX(-2px)' },
    { transform: 'translateX(2px)' },
    { transform: 'translateX(0)' }
  ],

  pulse: [
    { transform: 'scale(1)', opacity: '1' },
    { transform: 'scale(1.05)', opacity: '0.8' },
    { transform: 'scale(1)', opacity: '1' }
  ],

  fade: [
    { opacity: '1' },
    { opacity: '0.3' },
    { opacity: '1' }
  ],

  slide: [
    { transform: 'translateY(0)', opacity: '1' },
    { transform: 'translateY(-5px)', opacity: '0.8' },
    { transform: 'translateY(0)', opacity: '1' }
  ],

  bounce: [
    { transform: 'scale(1)' },
    { transform: 'scale(1.1)' },
    { transform: 'scale(0.95)' },
    { transform: 'scale(1.05)' },
    { transform: 'scale(1)' }
  ],

  glow: [
    { boxShadow: '0 0 0 0 rgba(66, 153, 225, 0)' },
    { boxShadow: '0 0 0 4px rgba(66, 153, 225, 0.3)' },
    { boxShadow: '0 0 0 0 rgba(66, 153, 225, 0)' }
  ],

  loading: [
    { opacity: '0.6' },
    { opacity: '1' },
    { opacity: '0.6' }
  ]
};

/**
 * Feedback animations manager
 */
export class FeedbackAnimations {
  private enabled: boolean;
  private respectsReducedMotion: boolean;
  private activeAnimations = new Map<HTMLElement, AnimationState>();
  private mediaQuery: MediaQueryList | null = null;

  constructor(enabled: boolean = true) {
    this.enabled = enabled;
    this.respectsReducedMotion = this.checkReducedMotionPreference();
    this.setupReducedMotionListener();
  }

  /**
   * Play error animation (shake effect)
   */
  public playErrorAnimation(element: HTMLElement, config?: Partial<AnimationConfig>): void {
    const animationConfig = {
      ...ANIMATION_PRESETS['error-shake'],
      ...config
    };

    this.playAnimation(element, animationConfig);
  }

  /**
   * Play success animation (glow effect)
   */
  public playSuccessAnimation(element: HTMLElement, config?: Partial<AnimationConfig>): void {
    const animationConfig = {
      ...ANIMATION_PRESETS['success-glow'],
      ...config
    };

    this.playAnimation(element, animationConfig);
  }

  /**
   * Play warning animation (fade effect)
   */
  public playWarningAnimation(element: HTMLElement, config?: Partial<AnimationConfig>): void {
    const animationConfig = {
      ...ANIMATION_PRESETS['warning-fade'],
      ...config
    };

    this.playAnimation(element, animationConfig);
  }

  /**
   * Play loading animation (pulse effect)
   */
  public playLoadingAnimation(element: HTMLElement, config?: Partial<AnimationConfig>): void {
    const animationConfig = {
      ...ANIMATION_PRESETS['loading-pulse'],
      ...config
    };

    this.playAnimation(element, animationConfig);
  }

  /**
   * Play focus animation (glow effect)
   */
  public playFocusAnimation(element: HTMLElement, config?: Partial<AnimationConfig>): void {
    const animationConfig = {
      ...ANIMATION_PRESETS['focus-glow'],
      ...config
    };

    this.playAnimation(element, animationConfig);
  }

  /**
   * Play custom animation
   */
  public playCustomAnimation(
    element: HTMLElement,
    keyframes: Keyframe[],
    config: AnimationConfig
  ): void {
    if (!this.enabled || this.shouldSkipAnimation()) {
      return;
    }

    this.clearAnimations(element);

    const animation = element.animate(keyframes, {
      duration: config.duration || 300,
      easing: config.easing || 'ease',
      delay: config.delay || 0,
      iterations: config.iterations || 1,
      direction: config.direction || 'normal'
    });

    this.trackAnimation(element, animation, config);
  }

  /**
   * Play predefined animation
   */
  public playAnimation(element: HTMLElement, config: AnimationConfig): void {
    if (!this.enabled || this.shouldSkipAnimation()) {
      return;
    }

    const keyframes = KEYFRAMES[config.type];
    if (!keyframes) {
      console.warn(`Unknown animation type: ${config.type}`);
      return;
    }

    this.clearAnimations(element);

    const animation = element.animate(keyframes, {
      duration: config.duration || 300,
      easing: config.easing || 'ease',
      delay: config.delay || 0,
      iterations: config.iterations || 1,
      direction: config.direction || 'normal'
    });

    this.trackAnimation(element, animation, config);
  }

  /**
   * Clear all animations for element
   */
  public clearAnimations(element: HTMLElement): void {
    const state = this.activeAnimations.get(element);
    if (state) {
      state.activeAnimations.forEach(animation => {
        animation.cancel();
      });
      this.activeAnimations.delete(element);
    }
  }

  /**
   * Check if element is currently being animated
   */
  public isAnimating(element: HTMLElement): boolean {
    return this.activeAnimations.has(element);
  }

  /**
   * Get current animation state for element
   */
  public getAnimationState(element: HTMLElement): AnimationState | null {
    return this.activeAnimations.get(element) || null;
  }

  /**
   * Update configuration
   */
  public updateConfig(enabled: boolean): void {
    this.enabled = enabled;
    
    if (!enabled) {
      // Clear all active animations
      this.activeAnimations.forEach((state, element) => {
        this.clearAnimations(element);
      });
    }
  }

  /**
   * Create animation sequence
   */
  public createSequence(
    element: HTMLElement,
    animations: Array<{ config: AnimationConfig; delay?: number }>
  ): Promise<void> {
    if (!this.enabled || this.shouldSkipAnimation()) {
      return Promise.resolve();
    }

    return animations.reduce((promise, { config, delay = 0 }) => {
      return promise.then(() => {
        return new Promise<void>(resolve => {
          setTimeout(() => {
            this.playAnimation(element, config);
            const state = this.activeAnimations.get(element);
            if (state && state.activeAnimations.length > 0) {
              state.activeAnimations[0].finished.then(() => resolve());
            } else {
              resolve();
            }
          }, delay);
        });
      });
    }, Promise.resolve());
  }

  /**
   * Create parallel animations
   */
  public createParallel(
    elements: HTMLElement[],
    config: AnimationConfig
  ): Promise<void[]> {
    if (!this.enabled || this.shouldSkipAnimation()) {
      return Promise.resolve([]);
    }

    const promises = elements.map(element => {
      this.playAnimation(element, config);
      const state = this.activeAnimations.get(element);
      return state && state.activeAnimations.length > 0 
        ? state.activeAnimations[0].finished.then(() => void 0)
        : Promise.resolve(void 0);
    });

    return Promise.all(promises);
  }

  /**
   * Animate state transition
   */
  public animateStateTransition(
    element: HTMLElement,
    fromState: string,
    toState: string,
    duration: number = 300
  ): void {
    if (!this.enabled || this.shouldSkipAnimation()) {
      return;
    }

    // Define transition animations based on state changes
    const transitions: Record<string, AnimationConfig> = {
      'default-to-error': ANIMATION_PRESETS['error-shake'],
      'error-to-valid': ANIMATION_PRESETS['success-glow'],
      'default-to-valid': ANIMATION_PRESETS['success-bounce'],
      'valid-to-error': ANIMATION_PRESETS['error-pulse'],
      'default-to-loading': ANIMATION_PRESETS['loading-pulse'],
      'loading-to-valid': ANIMATION_PRESETS['success-glow'],
      'loading-to-error': ANIMATION_PRESETS['error-shake']
    };

    const transitionKey = `${fromState}-to-${toState}`;
    const config = transitions[transitionKey];

    if (config) {
      this.playAnimation(element, { ...config, duration });
    }
  }

  /**
   * Check if should skip animations due to user preferences
   */
  private shouldSkipAnimation(): boolean {
    return this.respectsReducedMotion;
  }

  /**
   * Check for reduced motion preference
   */
  private checkReducedMotionPreference(): boolean {
    if (!window.matchMedia) {
      return false;
    }

    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Set up listener for reduced motion preference changes
   */
  private setupReducedMotionListener(): void {
    if (!window.matchMedia) {
      return;
    }

    this.mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      this.respectsReducedMotion = e.matches;
      
      if (this.respectsReducedMotion) {
        // Clear all active animations
        this.activeAnimations.forEach((state, element) => {
          this.clearAnimations(element);
        });
      }
    };

    this.mediaQuery.addEventListener('change', handleChange);
  }

  /**
   * Track active animation
   */
  private trackAnimation(
    element: HTMLElement,
    animation: Animation,
    config: AnimationConfig
  ): void {
    const state: AnimationState = {
      activeAnimations: [animation],
      element,
      config,
      startTime: Date.now(),
      respectsReducedMotion: this.respectsReducedMotion
    };

    this.activeAnimations.set(element, state);

    // Clean up when animation finishes
    animation.finished.then(() => {
      const currentState = this.activeAnimations.get(element);
      if (currentState) {
        const index = currentState.activeAnimations.indexOf(animation);
        if (index > -1) {
          currentState.activeAnimations.splice(index, 1);
          if (currentState.activeAnimations.length === 0) {
            this.activeAnimations.delete(element);
          }
        }
      }
    }).catch(() => {
      // Animation was cancelled, clean up
      this.activeAnimations.delete(element);
    });
  }

  /**
   * Cleanup all animations and listeners
   */
  public cleanup(): void {
    // Clear all active animations
    this.activeAnimations.forEach((state, element) => {
      this.clearAnimations(element);
    });

    // Remove media query listener
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener('change', () => {});
    }
  }
}

/**
 * Global animation manager instance
 */
let globalAnimationManager: FeedbackAnimations | null = null;

/**
 * Get global animation manager
 */
export function getFeedbackAnimations(enabled?: boolean): FeedbackAnimations {
  if (!globalAnimationManager) {
    globalAnimationManager = new FeedbackAnimations(enabled);
  }
  return globalAnimationManager;
}

/**
 * Create new animation manager
 */
export function createFeedbackAnimations(enabled?: boolean): FeedbackAnimations {
  return new FeedbackAnimations(enabled);
}

/**
 * Animation utility functions
 */
export const AnimationUtils = {
  /**
   * Check if browser supports Web Animations API
   */
  supportsAnimations(): boolean {
    return typeof Element !== 'undefined' && 'animate' in Element.prototype;
  },

  /**
   * Check if user prefers reduced motion
   */
  prefersReducedMotion(): boolean {
    return window.matchMedia && 
           window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  },

  /**
   * Get safe animation duration based on user preferences
   */
  getSafeDuration(requestedDuration: number): number {
    return AnimationUtils.prefersReducedMotion() ? 0 : requestedDuration;
  },

  /**
   * Create easing function for animations
   */
  createEasing(type: 'ease' | 'bounce' | 'elastic'): string {
    switch (type) {
      case 'bounce':
        return 'cubic-bezier(0.68, -0.55, 0.265, 1.55)';
      case 'elastic':
        return 'cubic-bezier(0.175, 0.885, 0.32, 1.275)';
      default:
        return 'ease';
    }
  }
};